import React from "react";
import ReactMarkdown from "react-markdown";
import { ClaudeMessage } from "@/utils/axios/claudeUtils.ts";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { motion } from "framer-motion"; // Import direct des composants
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism"; // Importer quelques langages courants (vous pouvez en ajouter d'autres selon vos besoins)
import javascript from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import typescript from "react-syntax-highlighter/dist/esm/languages/prism/typescript";
import bash from "react-syntax-highlighter/dist/esm/languages/prism/bash";
import css from "react-syntax-highlighter/dist/esm/languages/prism/css";
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import java from "react-syntax-highlighter/dist/esm/languages/prism/java";
import "github-markdown-css/github-markdown-light.css";
import "katex/dist/katex.min.css";
import { CheckCircle, Copy } from "lucide-react";
import AudioIconPlayer from "@/components/tools/Polly/AudioIconPlayer.tsx"; // Enregistrer les langages

// Enregistrer les langages
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("typescript", typescript);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("css", css);
SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("java", java);

const getContentToRead = (message: ClaudeMessage) => {
  if (!message || !message.content) {
    return "";
  }

  let content = message.content;

  // Filtrer tous les blocs de code avec leurs annotations de langage
  content = content.replace(
    /```[\s\S]*?```/g,
    "Code supprimé pour la lecture vocale."
  );

  // Filtrer les lignes de code inline
  content = content.replace(/`[^`]+`/g, "");

  // Suppression des liens markdown
  content = content.replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1");

  // Supprimer les images markdown
  content = content.replace(/!\[.*?\]\(.*?\)/g, "");

  // Supprimer les tableaux markdown (lignes qui commencent par | et contiennent plusieurs |)
  const lines = content.split("\n");
  const filteredLines = lines.filter((line) => {
    // Exclure les lignes de tableau (qui commencent par | et contiennent au moins un autre |)
    const isTableLine =
      line.trim().startsWith("|") && line.trim().indexOf("|", 1) !== -1;
    // Exclure les lignes de séparation des tableaux (qui contiennent seulement | et -)
    const isTableDivider = /^\s*\|[\s\-\|]+\|\s*$/.test(line);

    return !isTableLine && !isTableDivider;
  });
  content = filteredLines.join("\n");

  // Supprimer les en-têtes markdown (# Titre)
  content = content.replace(/^(#{1,6})\s+(.+)$/gm, "$2");

  // Supprimer les listes numérotées (1. Item)
  content = content.replace(/^\s*\d+\.\s+/gm, "• ");

  // Supprimer les listes non numérotées
  content = content.replace(/^\s*[-*•]\s+/gm, "• ");

  // Supprimer les éléments de mise en forme
  content = content.replace(/(\*\*|__)(.*?)\1/g, "$2"); // Gras
  content = content.replace(/(\*|_)(.*?)\1/g, "$2"); // Italique
  content = content.replace(/~~(.*?)~~/g, "$1"); // Barré

  // Remplacer les sauts de ligne multiples par un unique saut
  content = content.replace(/\n{3,}/g, "\n\n");

  // Décodage des entités HTML courantes
  content = content
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");

  return content.trim();
};

interface MessageItemProps {
  message: ClaudeMessage;
}

const MessageItem: React.FC<MessageItemProps> = ({ message }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isAssistant = message.role === "assistant";
  const messageVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  };

  return (
    <motion.div
      className={`chat ${isAssistant ? "chat-start" : "chat-end"} my-2 md:my-3`}
      initial="hidden"
      animate="visible"
      variants={messageVariants}
    >
      <div
        className={`chat-header opacity-70 text-xs mb-1 ${
          isAssistant ? "text-left" : "text-right"
        }`}
      >
        {message.timestamp && (
          <div className="flex items-center">
            {isAssistant ? "Assistant" : "Vous"}
            {isAssistant && (
              <AudioIconPlayer text={getContentToRead(message)} size="sm" />
            )}
            <span className="ml-2">
              {new Date(message.timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>{" "}
            <span className="ml-2">
              {new Date(message.timestamp).toLocaleDateString([], {
                day: "2-digit",
                month: "2-digit",
              })}
            </span>
          </div>
        )}
      </div>
      <div
        className={`chat-bubble rounded-2xl ${
          isAssistant
            ? "chat-bubble-secondary bg-gray-50 text-gray-800 shadow-[0_0_10px_rgba(0,0,0,0.05)]"
            : "bg-purple-500 bg-opacity-90 text-white shadow-[0_0_15px_rgba(107,33,168,0.1)]"
        } max-w-[90%] sm:max-w-[85%] md:max-w-[75%] p-2 border-0`}
        style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
      >
        {isAssistant ? (
          <div className="markdown-body bg-transparent text-sm md:text-base">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeKatex]}
              components={{
                code({ _, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || "");
                  const language = match ? match[1] : "";
                  const rawCode = String(children).replace(/\n$/, "");

                  if (!inline && language) {
                    return (
                      <div className="relative group">
                        <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          <button
                            onClick={() => copyToClipboard(rawCode)}
                            className="p-1 bg-gray-800 text-white rounded-full hover:bg-gray-700 transition-colors"
                            title="Copier le code"
                          >
                            {copied ? (
                              <CheckCircle
                                size={16}
                                className="text-green-400"
                              />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                        </div>
                        {/* @ts-expect-error Voir la version de react-syntax-highlighter et son utilisation */}
                        <SyntaxHighlighter
                          style={vscDarkPlus}
                          language={language}
                          className="rounded-xl !bg-[#1E1E1E] !text-[#EEFFFF] text-xs md:text-sm border-0 shadow-sm"
                          wrapLines={true}
                          showLineNumbers={
                            language !== "bash" && language !== "shell"
                          }
                          customStyle={{
                            margin: 0,
                            padding: "0.5rem",
                            borderRadius: "0.5rem",
                          }}
                          {...props}
                        >
                          {rawCode}
                        </SyntaxHighlighter>
                      </div>
                    );
                  }

                  return (
                    <code
                      className="bg-gray-100 rounded-md px-1.5 py-0.5 text-purple-900 font-medium text-xs md:text-sm"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                img({ _, ...props }) {
                  return (
                    <img
                      className="max-w-full h-auto rounded-md my-2"
                      {...props}
                      loading="lazy"
                    />
                  );
                },
                table({ _, children, ...props }) {
                  return (
                    <div className="overflow-x-auto mb-2 rounded-lg shadow-sm">
                      <table
                        className="border-collapse w-full text-xs md:text-sm"
                        {...props}
                      >
                        {children}
                      </table>
                    </div>
                  );
                },
                pre({ _, children, ...props }) {
                  return (
                    <pre className="overflow-x-auto max-w-full" {...props}>
                      {children}
                    </pre>
                  );
                },
                a({ _, children, ...props }) {
                  return (
                    <a
                      className="text-purple-600 hover:text-purple-800 hover:underline"
                      {...props}
                    >
                      {children}
                    </a>
                  );
                },
                p({ _, children, ...props }) {
                  return (
                    <p
                      className="mb-2 last:mb-0 text-sm md:text-base"
                      {...props}
                    >
                      {children}
                    </p>
                  );
                },
                ul({ _, children, ...props }) {
                  return (
                    <ul
                      className="list-disc pl-3 mb-2 space-y-1 text-sm md:text-base"
                      {...props}
                    >
                      {children}
                    </ul>
                  );
                },
                ol({ _, children, ...props }) {
                  return (
                    <ol
                      className="list-decimal pl-3 mb-2 space-y-1 text-sm md:text-base"
                      {...props}
                    >
                      {children}
                    </ol>
                  );
                },
                th({ _, children, ...props }) {
                  return (
                    <th
                      className="border border-gray-200 bg-gray-100 px-2 py-1 text-left"
                      {...props}
                    >
                      {children}
                    </th>
                  );
                },
                td({ _, children, ...props }) {
                  return (
                    <td className="border border-gray-200 px-4 py-1" {...props}>
                      {children}
                    </td>
                  );
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="whitespace-pre-wrap text-sm md:text-base">
            {message.content}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MessageItem;
